import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {S3DirectUploadService} from '@app/cloud-run-prep/analysis-setup/services/s3-direct-upload.service';
import {catchAndThrowGSSApiError, genericRetryWhen} from '@app/core/rxjsutils/rxjs-utilities';
import {CreateReferenceFileRequest, ReferenceFile, ReferenceFilesService, UpdateReferenceFileRequest} from '@stratus/gss-ng-sdk';
import {from, of} from 'rxjs';
import {flatMap, retryWhen} from 'rxjs/operators';
import {DirectUploadInfo, IcaFileUploadService, IcaFolderType} from '@app/shared/ica-file-upload-service';
import {DataLocationUri} from '../data-location-uri';
import {
  ReferenceFileCompactReferenceFileSortFieldsPagedItems
} from '@stratus/gss-ng-sdk/api/models/reference-file-compact-reference-file-sort-fields-paged-items';
import { isEmpty } from 'lodash';
import { ReferenceFileStatus } from '@stratus/gss-ng-sdk/api/models/reference-file-status';

export interface FileToReplace {
  fileId: string;
}
export interface ReferenceFileDetails {
  fileName?: string;
  description?: string; 
  displayName?: string;
  dataLocationUri?: string;
  supportedGenomeIds?: string[]; 
  type?: string;
  tag?: string;
  fileSizeInBytes?: number;
  status?: ReferenceFileStatus;
}

@Injectable({
  providedIn: 'root'
})
export class ReferenceFileUploadService extends IcaFileUploadService {
  static readonly REFERENCE_FILE_CUSTOM_FOLDER = 'custom';

  protected constructor(
    httpClient: HttpClient,
    s3FileUploadService: S3DirectUploadService,
    private referenceFilesService: ReferenceFilesService) {
    super(IcaFolderType.ReferenceFiles, httpClient, s3FileUploadService);
  }

  /**
   * Returns the reference files with the given file name.
   */
  public async getReferenceFiles(fileName?: string): Promise<ReferenceFileCompactReferenceFileSortFieldsPagedItems> {
    return this.referenceFilesService.listReferenceFiles({name: fileName}).toPromise();
  }

  /**
   * get direct upload info, including temporary upload credentials
   */
  public async getDirectUploadInfo(): Promise<DirectUploadInfo> {
    return super.getDirectUploadInfo(ReferenceFileUploadService.REFERENCE_FILE_CUSTOM_FOLDER);
  }

  /**
   * Creates a new reference file and add metadata to GSS.
   */
  public async uploadOrReplaceReferenceFile(file: File, info: DirectUploadInfo, 
    fileToReplace?: FileToReplace, fileDetails?: ReferenceFileDetails): Promise<ReferenceFile> {
    const eTag = await this.uploadFile(info.tempUploadCredential, file);
    fileDetails = fileDetails || {};
    fileDetails.tag = eTag;
    return this.onUploadCompleted(info, file, fileToReplace, fileDetails);
  }

  /**
   * On upload completed, need to close upload session and get the file urn from ICA
   * File metadata on GSS is only created for new file
   * @returns file as in IReferenceFile format
   */
  private async onUploadCompleted(info: DirectUploadInfo, file: File, fileToReplace?: FileToReplace,
    fileDetails?: ReferenceFileDetails
  ): Promise<ReferenceFile> {
    const uploadedFileNames = [file.name];
    const uploadedFiles = await super.completeUploadSession(info.uploadSessionId,
      ReferenceFileUploadService.REFERENCE_FILE_CUSTOM_FOLDER, uploadedFileNames);
    const uploadedFile = uploadedFiles[0];
    fileDetails = fileDetails || {};
    
    // Post request to create gss reference file, using urn instead of dataLocationUri
    if (fileToReplace) {
      // Returns file ID to replace if a file with the same name already exists
      const referenceFileId = fileToReplace.fileId;
      fileDetails.fileSizeInBytes = file.size;
      // Update eTag and file size in bytes in GSS
      return this.updateReferenceFileMetaDataOnGss(referenceFileId, fileDetails);
    } else { // Create new metadata on GSS
      const fileNameWithoutSpace = file.name.replace(/ /g, '_');

      let urnOrUri = null;
      if (uploadedFile.data.urn) {
        urnOrUri = uploadedFile.data.urn;
      } else {
        urnOrUri = new DataLocationUri(info.projectId, uploadedFile.data.id, fileNameWithoutSpace).toString();
      }

      if (!urnOrUri) {
        throw new Error('Unable to retrieve DataLocationUrn/Uri for the reference file.');
      }

      fileDetails.fileName = fileNameWithoutSpace;
      fileDetails.fileSizeInBytes = file.size;
      fileDetails.dataLocationUri = urnOrUri;
      fileDetails.status = 'Ready';
      return this.createReferenceFileMetaDataOnGss(fileDetails)
        .catch (err => {
          // try to delete the uploaded file (deletion failure is not handled)
          this.deleteData(uploadedFile.data.id)
            .catch(deleteErr => {
              console.error(deleteErr);
            });

          // throw original error to the caller
          throw err;
        });
    }
  }

  /**
   * Construct request and post to GSS to create reference file metadata
   */
  public async createReferenceFileMetaDataOnGss(fileDetails: ReferenceFileDetails): Promise<ReferenceFile> {
    const request: CreateReferenceFileRequest = {
      name: fileDetails.fileName, dataLocationUri: fileDetails.dataLocationUri,
      checksum: fileDetails.tag, fileSizeInBytes: fileDetails.fileSizeInBytes, type: fileDetails.type,
      description: fileDetails.description, displayName: fileDetails.displayName, 
      supportedGenomeIds: fileDetails.supportedGenomeIds,
      status: fileDetails.status,
    };
    this.removeEmpty(request);
    return this.referenceFilesService.createReferenceFile(request).toPromise();
  }

  /**
   * Construct request and patch to GSS to update reference file etag and file size
   */
  public async updateReferenceFileMetaDataOnGss(referenceFileId: string,
    fileDetails: ReferenceFileDetails): Promise<ReferenceFile> {
    const updateReferenceFileRequest: UpdateReferenceFileRequest = {
      checksum: fileDetails.tag, fileSizeInBytes: fileDetails.fileSizeInBytes, name: fileDetails.fileName, 
      dataLocationUri: fileDetails.dataLocationUri, 
      description: fileDetails.description, displayName: fileDetails.displayName, 
      supportedGenomeIds: fileDetails.supportedGenomeIds, type: fileDetails.type,
      status: fileDetails.status,
    };
    this.removeEmpty(updateReferenceFileRequest);
    return this.referenceFilesService.updateReferenceFile(
      {referenceFileId, body: updateReferenceFileRequest})
      .toPromise();
  }

  /**
   * Delete reference-file's metadata (in GSS) and content (in ICA)
   */
  public async deleteReferenceFile(referenceFileId: string, dataId: string): Promise<any> {
    return this.referenceFilesService.deleteReferenceFile({referenceFileId}).pipe(
      retryWhen(genericRetryWhen()),
      flatMap(() => from(dataId ? super.deleteData(dataId) : of(true))),
      catchAndThrowGSSApiError
    ).toPromise();
  }

  private removeEmpty(obj: any) {
    if (isEmpty(obj)) {
      return;
    }
  
    Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object') {
        this.removeEmpty(obj[key]);
      } else if (obj[key] == null || obj[key] === '') {
        delete obj[key];
      }
    });
  }
}
